import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface DataPoint {
  [key: string]: any;
}

interface BarChartComponentProps {
  data: DataPoint[];
  xField: string;
  yFields: string[];
  yFieldNames: string[];
  title: string;
  xTickAngle?: number;
  height?: number;
  colors?: string[];
  showLegend?: boolean;
}

const BarChartComponent: React.FC<BarChartComponentProps> = ({
  data,
  xField,
  yFields,
  yFieldNames,
  title,
  xTickAngle = -45,
  height = 400,
  colors = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#0088fe"],
  showLegend = true,
}) => {
  if (!data || data.length === 0) {
    return (
      <div>
        <h5>{title}</h5>
        <div>No data Available</div>
      </div>
    );
  }

  return (
    <div>
      <h5>{title}</h5>
      <div style={{ width: "100%", height: `${height}px` }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={xField}
              angle={xTickAngle}
              textAnchor="end"
              height={70}
            />
            <YAxis />
            <Tooltip />
            {showLegend && (
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="center"
                wrapperStyle={{ paddingBottom: 10 }}
              />
            )}
            {yFields.map((field, index) => (
              <Bar
                key={field}
                dataKey={field}
                name={yFieldNames[index]}
                fill={colors[index % colors.length]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

interface SimpleBarChartComponentProps {
  data: { name: string; value: number }[];
  title: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  color?: string;
  xTickAngle?: number;
}

export const SimpleBarChartComponent: React.FC<
  SimpleBarChartComponentProps
> = ({
  data,
  title,
  xAxisLabel = "",
  yAxisLabel = "Count",
  height = 400,
  color = "#8884d8",
  xTickAngle = -45,
}) => {
  if (!data || data.length === 0) {
    return (
      <div>
        <h5>{title}</h5>
        <div>No data available</div>
      </div>
    );
  }

  return (
    <div>
      <h5>{title}</h5>
      <div style={{ width: "100%", height: `${height}px` }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 30, bottom: 70 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={xTickAngle}
              textAnchor="end"
              height={70}
              fontSize={12}
              label={
                xAxisLabel
                  ? { value: xAxisLabel, position: "insideBottom", offset: -10 }
                  : undefined
              }
            />
            <YAxis
              label={
                yAxisLabel
                  ? {
                      value: yAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      offset: -10,
                    }
                  : undefined
              }
            />
            <Tooltip formatter={(value) => [`${value}`, yAxisLabel]} />
            <Bar dataKey="value" name={yAxisLabel} fill={color} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChartComponent;
